// Lightweight fuzzy matcher for offline vendor / product lookup. No deps — a
// normalized bigram Dice coefficient with a token-containment boost, enough to
// bridge show-floor spellings to our records (e.g. "AMAZON" ⇄ "AMAZON.COM",
// "Twiddle Sleeve" ⇄ "TWIDDLE SLV"). Every result is a *candidate* the user must
// confirm — we never assert a hard match (matched_vendor stays user-confirmed).

/** Lowercase, strip punctuation, collapse whitespace. */
export function normalize(s: string): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function bigrams(s: string): string[] {
  const t = s.replace(/\s+/g, '');
  const out: string[] = [];
  for (let i = 0; i < t.length - 1; i++) out.push(t.slice(i, i + 2));
  return out;
}

/** Similarity in [0,1]: 1 = identical (normalized), 0 = nothing in common. */
export function similarity(query: string, target: string): number {
  const a = normalize(query);
  const b = normalize(target);
  if (!a || !b) return 0;
  if (a === b) return 1;

  // Token containment: every query token present in the target (or vice-versa)
  // — catches short queries inside longer names ("amazon" ⊂ "amazon com").
  const at = a.split(' ');
  const bt = b.split(' ');
  const contained = at.every((t) => bt.includes(t) || b.includes(t)) || bt.every((t) => a.includes(t));

  const ba = bigrams(a);
  const bb = bigrams(b);
  if (ba.length === 0 || bb.length === 0) {
    // Single-char tokens — fall back to substring.
    return a.includes(b) || b.includes(a) ? 0.85 : 0;
  }
  const counts = new Map<string, number>();
  for (const g of ba) counts.set(g, (counts.get(g) ?? 0) + 1);
  let inter = 0;
  for (const g of bb) {
    const c = counts.get(g);
    if (c && c > 0) {
      inter++;
      counts.set(g, c - 1);
    }
  }
  const dice = (2 * inter) / (ba.length + bb.length);
  // A containment match is a strong signal even if the strings differ in length.
  return Math.min(1, contained ? Math.max(dice, 0.7) : dice);
}

export interface Ranked<T> {
  item: T;
  score: number;
}

/**
 * Rank `items` against `query` by similarity of `keyFn(item)`. Empty query
 * returns the first `limit` items unscored (score 1) for browse; the caller
 * pre-sorts for browse order. Non-empty query filters by `threshold` and sorts
 * best-first.
 */
export function rankMatches<T>(
  query: string,
  items: readonly T[],
  keyFn: (t: T) => string,
  opts: { limit?: number; threshold?: number } = {},
): Ranked<T>[] {
  const { limit = 25, threshold = 0.3 } = opts;
  if (!query.trim()) return items.slice(0, limit).map((item) => ({ item, score: 1 }));
  const scored: Ranked<T>[] = [];
  for (const item of items) {
    const score = similarity(query, keyFn(item));
    if (score >= threshold) scored.push({ item, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit);
}

/** Human confidence label — always "likely", never a hard claim. */
export function confidence(score: number): 'strong' | 'likely' | 'weak' {
  if (score >= 0.85) return 'strong';
  if (score >= 0.55) return 'likely';
  return 'weak';
}
